import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
// @ts-ignore - throttle lacks TS declarations
import throttle from 'lodash-es/throttle';
import Icon from 'flarum/common/components/Icon';
import classList from 'flarum/common/utils/classList';
import Stream from 'flarum/common/utils/Stream';

/**
 * Adds a typing indicator to the MessageStream component when flarum/realtime
 * is enabled. Binds to a per-dialog private Pusher channel to send/receive
 * `client-typing` events.
 */
export default function addRealtimeTypingIndicator() {
  extend('ext:flarum/messages/forum/components/MessageStream', 'content', function (items) {
    var _app$session;
    const typingUsers = Object.keys(this.getTypingUsers());
    const count = typingUsers.length;
    const max = 3;
    const classes = classList(['TypingUsersContainer', count > 0 && 'TypingUsersContainer-active']);
    const typingIcon = count > 0 ? 'fas fa-ellipsis-h fa-beat' : 'fas fa-pause';
    const namedUsers = typingUsers.slice(0, max).join(', ');
    let showUsers = true;
    if ((_app$session = app.session) != null && _app$session.user) {
      var _preferences;
      showUsers = (_preferences = app.session.user.preferences()) == null ? void 0 : _preferences['flarum-realtime.typing-indicator-full'];
    }
    items.splice(items.length - 1, 0, m("div", {
      className: classes,
      key: "typing"
    }, m("div", {
      className: "TypingUsers"
    }, m(Icon, {
      name: typingIcon
    }), count > 0 ? showUsers ? app.translator.trans('flarum-realtime.forum.typing-indicator.users-are-typing', {
      users: namedUsers,
      count: count,
      others: Math.max(count - max, 0)
    }) : app.translator.trans('flarum-realtime.forum.typing-indicator.people-are-typing', {
      number: count
    }) : app.translator.trans('flarum-realtime.forum.typing-indicator.no-activity'))));
  });
  extend('ext:flarum/messages/forum/components/MessageStream', 'oninit', function () {
    this.previousContent = Stream('');
    this.usersTyping = Stream({});
    this.typingTruncationListener = null;
    this.typingListener = null;
    this.getTypingUsers = function () {
      const invalidateWhen = new Date().getTime() - 3500;
      const users = this.usersTyping();
      let timeout = null;
      for (const displayName in users) {
        const time = users[displayName];
        if (time < invalidateWhen) {
          delete users[displayName];
        } else if (!timeout || timeout < time) {
          timeout = time;
        }
      }
      this.usersTyping(users);
      if (timeout && this.typingTruncationListener) {
        clearTimeout(this.typingTruncationListener);
      }
      if (timeout) {
        this.typingTruncationListener = setTimeout(function () {
          m.redraw();
        }.bind(this), timeout - new Date().getTime());
      }
      return users;
    };
    this.userTyping = function (data) {
      if (!data.discloseOnline) {
        return;
      }
      const users = this.usersTyping();
      users[data.displayName] = data.time;
      this.usersTyping(users);
      m.redraw();
    };
    this.actorIsTyping = function () {
      var _app$session$user, _websocket_channels, _app$session$user2;
      const discloseOnline = (_app$session$user = app.session.user) == null || (_app$session$user = _app$session$user.preferences()) == null ? void 0 : _app$session$user.discloseOnline;
      (_websocket_channels = app.websocket_channels) == null || (_websocket_channels = _websocket_channels.privateMessages) == null || _websocket_channels.trigger('client-typing', {
        displayName: discloseOnline ? (_app$session$user2 = app.session.user) == null ? void 0 : _app$session$user2.displayName() : '[anonymous]',
        discloseOnline,
        time: Date.now()
      });
    };
    this.checkTyping = function () {
      var _composer;
      if (this.previousContent() !== ((_composer = app.composer) == null || (_composer = _composer.fields) == null ? void 0 : _composer.content())) {
        var _composer2;
        this.actorIsTyping();
        this.previousContent((_composer2 = app.composer) == null || (_composer2 = _composer2.fields) == null ? void 0 : _composer2.content());
      }
    };
  });
  extend('ext:flarum/messages/forum/components/MessageStream', 'oncreate', function () {
    var _forum, _this$attrs, _websocket, _websocket_channels$p;
    if ((_forum = app.forum) != null && _forum.attribute('websocket.disallow_connection')) return;
    if (!((_this$attrs = this.attrs) != null && _this$attrs.dialog)) return;
    this.typingListener = throttle(function () {
      this.checkTyping();
    }.bind(this), 2000);
    this.typingListener = setInterval(this.typingListener, 1000);
    app.websocket_channels = app.websocket_channels || {};
    app.websocket_channels.privateMessages = (_websocket = app.websocket) == null ? void 0 : _websocket.subscribe('private-privateMessageTyping=' + this.attrs.dialog.id());
    (_websocket_channels$p = app.websocket_channels.privateMessages) == null || _websocket_channels$p.bind('client-typing', data => {
      this.userTyping(data);
    });
  });
  extend('ext:flarum/messages/forum/components/MessageStream', 'onremove', function () {
    var _websocket_channels2;
    if (this.typingListener) clearInterval(this.typingListener);
    if (this.typingTruncationListener) clearTimeout(this.typingTruncationListener);
    (_websocket_channels2 = app.websocket_channels) == null || (_websocket_channels2 = _websocket_channels2.privateMessages) == null || _websocket_channels2.unsubscribe();
  });
}